import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';

// Predefined color palette for categories
const CATEGORY_COLORS = [
  { bg: '#dbeafe', text: '#1d4ed8' }, // Blue (NSK)
  { bg: '#f3e8ff', text: '#7c3aed' }, // Purple (OTHER)
  { bg: '#dcfce7', text: '#166534' }, // Green
  { bg: '#fef3c7', text: '#92400e' }, // Yellow
  { bg: '#fce7f3', text: '#be185d' }, // Pink
  { bg: '#e0e7ff', text: '#4338ca' }, // Indigo
  { bg: '#fef2f2', text: '#991b1b' }, // Red
  { bg: '#ecfdf5', text: '#065f46' }, // Emerald
  { bg: '#fff7ed', text: '#9a3412' }, // Orange
  { bg: '#f0f9ff', text: '#0c4a6e' }, // Sky
  { bg: '#f5f3ff', text: '#5b21b6' }, // Violet
  { bg: '#fefce8', text: '#713f12' }, // Lime
];

// Generate a stable color for a category based on its name
const getCategoryColor = (categoryName) => {
  // Use predefined colors for known categories
  const lowerName = categoryName.toLowerCase();
  if (lowerName === 'nsk') {
    return CATEGORY_COLORS[0];
  }
  if (lowerName === 'other') {
    return CATEGORY_COLORS[1];
  }
  
  // For other categories, use a hash function to get a stable color
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[colorIndex];
};

const ReceiptQuantityTab = forwardRef(function ReceiptQuantityTab({ config, onUpdate, hideWizardSaveBar = false }, ref) {
  const [formData, setFormData] = useState({
    maxTotalReceipts: 3,
    categoryLimits: {}
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const categories = config?.tenantCategories || [];

  useEffect(() => {
    // Initialize formData with all categories, using existing values or 1 as default
    const existingRules = config?.receiptQuantityRules || {};
    const existingLimits = existingRules.categoryLimits || {};
    const initialLimits = {};
    categories.forEach(cat => {
      initialLimits[cat] = existingLimits[cat] !== undefined ? existingLimits[cat] : 1;
    });
    setFormData({
      maxTotalReceipts: existingRules.maxTotalReceipts || 3,
      categoryLimits: initialLimits
    });
  }, [config, categories.join(',')]);

  const isDirty = useMemo(() => {
    const saved = config?.receiptQuantityRules || {};
    const savedLimits = saved.categoryLimits || {};
    const maxSaved = saved.maxTotalReceipts ?? 3;
    const maxFormRaw = formData.maxTotalReceipts;
    if (maxFormRaw === '') return true;
    const maxForm = parseInt(maxFormRaw, 10);
    if (Number.isNaN(maxForm) || maxForm !== maxSaved) return true;
    for (const cat of categories) {
      const lim = formData.categoryLimits[cat];
      if (lim === '') return true;
      const limNum = parseInt(lim, 10);
      const limSaved = savedLimits[cat] !== undefined ? savedLimits[cat] : 1;
      if (Number.isNaN(limNum) || limNum !== limSaved) return true;
    }
    return false;
  }, [config?.receiptQuantityRules, categories.join(','), formData.maxTotalReceipts, formData.categoryLimits]);

  const handleSave = async () => {
    // Validate before saving
    if (!isValidConfiguration()) {
      setMessage({ 
        type: 'error', 
        text: getValidationMessage()
      });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({ receiptQuantityRules: formData });
      setMessage({ type: 'success', text: 'Receipt quantity rules saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryLimitChange = (category, value) => {
    // Allow empty value for editing
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        categoryLimits: {
          ...prev.categoryLimits,
          [category]: ''
        }
      }));
      return;
    }
    
    // Only allow digits
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Remove leading zeros, minimum value is 0 for display but validation ensures > 0
    const noLeadingZeros = cleanValue.replace(/^0+/, '') || '0';
    
    const numValue = parseInt(noLeadingZeros, 10) || 0;
    
    setFormData(prev => ({
      ...prev,
      categoryLimits: {
        ...prev.categoryLimits,
        [category]: numValue
      }
    }));
  };

  const handleOverallLimitChange = (value) => {
    // Allow empty value for editing
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        maxTotalReceipts: ''
      }));
      return;
    }
    
    // Only allow digits
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Remove leading zeros but keep at least one digit
    const noLeadingZeros = cleanValue.replace(/^0+/, '') || '1';
    
    const numValue = parseInt(noLeadingZeros, 10) || 1;
    
    setFormData(prev => ({
      ...prev,
      maxTotalReceipts: numValue
    }));
  };

  // Check if validation is passing
  // Rule: Each category limit must be <= overall limit (since users can only submit one category at a time)
  const isValidConfiguration = () => {
    const maxTotal = parseInt(formData.maxTotalReceipts, 10) || 0;
    
    if (maxTotal <= 0) return false;
    
    // Check that all category limits are > 0 and <= maxTotalReceipts
    const allCategoryLimitsValid = Object.values(formData.categoryLimits).every(val => {
      const numVal = parseInt(val, 10);
      return !isNaN(numVal) && numVal > 0 && numVal <= maxTotal;
    });
    
    return allCategoryLimitsValid;
  };

  // Find categories that exceed the overall limit
  const getCategoriesExceedingLimit = () => {
    const maxTotal = parseInt(formData.maxTotalReceipts, 10) || 0;
    return Object.entries(formData.categoryLimits)
      .filter(([_, val]) => {
        const numVal = parseInt(val, 10);
        return !isNaN(numVal) && numVal > maxTotal;
      })
      .map(([cat, val]) => ({ category: cat, limit: val }));
  };

  useImperativeHandle(ref, () => ({
    saveStep: async () => {
      if (!isValidConfiguration()) {
        return { ok: false, error: getValidationMessage() || 'Invalid receipt quantity rules.' };
      }
      setSaving(true);
      setMessage(null);
      try {
        await onUpdate({ receiptQuantityRules: formData });
        return { ok: true };
      } catch {
        return { ok: false, error: 'Failed to save receipt quantity rules.' };
      } finally {
        setSaving(false);
      }
    }
  }), [formData, onUpdate]);

  const getValidationMessage = () => {
    const maxTotal = parseInt(formData.maxTotalReceipts, 10) || 0;
    
    // Check if any category limit is 0 or empty
    const invalidCategories = Object.entries(formData.categoryLimits)
      .filter(([_, val]) => {
        const numVal = parseInt(val, 10);
        return isNaN(numVal) || numVal <= 0;
      })
      .map(([cat, _]) => cat);
    
    if (invalidCategories.length > 0) {
      return `Category limits must be greater than 0: ${invalidCategories.join(', ')}`;
    }
    
    // Check if any category limit exceeds overall limit
    const exceedingCategories = getCategoriesExceedingLimit();
    if (exceedingCategories.length > 0) {
      const details = exceedingCategories.map(c => `${c.category} (${c.limit})`).join(', ');
      return `Each category limit must be ≤ overall limit (${maxTotal}). Exceeding: ${details}`;
    }
    
    return null;
  };

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Receipt Quantity Rules</h2>
        <p>Control how many receipts can be submitted per claim and per tenant category.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Overall Limit</div>
          <div className="form-group">
            <label>Maximum Total Receipts per Claim</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.maxTotalReceipts}
              onChange={(e) => handleOverallLimitChange(e.target.value)}
              placeholder="1"
            />
            <span className="form-hint">The maximum number of receipts a user can submit in a single claim.</span>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">Category Limits</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
            Set maximum receipts allowed per tenant category in a single claim.
          </p>
          <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: '0 0 1rem 0', fontWeight: '500' }}>
            ⚠️ Note: Users can only submit receipts from ONE category per claim. Each category limit must be ≤ overall limit.
          </p>
          
          <div className="form-row">
            {categories.map(category => (
              <div key={category} className="form-group">
                <label>
                  <span 
                    className="category-badge" 
                    style={{ 
                      marginRight: '0.5rem',
                      background: getCategoryColor(category).bg,
                      color: getCategoryColor(category).text
                    }}
                  >
                    {category}
                  </span>
                  Max Receipts
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.categoryLimits[category] === '' ? '' : formData.categoryLimits[category]}
                  onChange={(e) => handleCategoryLimitChange(category, e.target.value)}
                  placeholder="0"
                />
              </div>
            ))}
          </div>
          
          {/* Validation Warning */}
          {!isValidConfiguration() && (
            <div className="validation-warning" style={{
              marginTop: '1rem',
              padding: '0.875rem 1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#991b1b',
              fontSize: '0.875rem'
            }}>
              <span style={{ fontSize: '1.125rem' }}>⚠️</span>
              <span>{getValidationMessage()}</span>
            </div>
          )}
          
          {/* Info Display */}
          <div style={{
            marginTop: '1rem',
            padding: '0.875rem 1rem',
            background: isValidConfiguration() ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${isValidConfiguration() ? '#86efac' : '#fecaca'}`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            color: isValidConfiguration() ? '#166534' : '#991b1b'
          }}>
            <span style={{ fontSize: '1.125rem' }}>
              {isValidConfiguration() ? '✓' : '✗'}
            </span>
            <span>
              {isValidConfiguration() 
                ? `All category limits are valid (≤ ${formData.maxTotalReceipts})`
                : `Each category limit must be between 1 and ${formData.maxTotalReceipts}`
              }
            </span>
          </div>
        </div>

        <div className="form-section" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.25rem' }}>💡</span>
            <div>
              <strong style={{ color: '#92400e' }}>How It Works</strong>
              <p style={{ color: '#a16207', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                Users can only submit receipts from <strong>one category</strong> per claim. For example, if overall limit = 3 and NSK limit = 2, a user choosing NSK can only submit up to 2 receipts (even though overall limit is 3).
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !isDirty || !isValidConfiguration()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default ReceiptQuantityTab;
